
import { computed, defineComponent, onMounted, PropType, reactive, ref, toRaw, watch } from 'vue'

// components
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheTextarea from '@/common/components/UI/TheTextarea/index.vue'
import Checkbox from '@/common/components/UI/Checkbox/index.vue'
import RadioGroup from '@/common/components/UI/RadioGroup/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'
import FileReader from '@/common/components/UI/FileReader/index.vue'

// module components
import UploadedFile from '@/modules/InvoiceModule/components/UploadedFile/index.vue'

// constants
import {
    LTL_SHIPMENT_TYPES,
    PAYMENT_METHOD_OPTIONS,
    PAYMENT_TYPE_OPTIONS,
} from '@/core/constants/create_order.constants'

// validation
import useVuelidate from '@vuelidate/core'
import { required, requiredIf, minLength, maxLength, decimal, integer } from '@vuelidate/validators'

// mask
import { PRICE_MASK } from '@/core/constants/masks.constants'

// types
import { CompletedCargoT, UploadedFileT, TemperatureRegimeT } from '@/core/types/create_order.types'
import { UpdateInvoicePayloadT, UplodaFilesResponseT } from '@/core/types/invoices.types'
import { PayerT } from '@/core/types/companies.types'
import { ReceiverT } from '@/core/types/receivers.types'

// realisations
import { invoices } from '@/core/realisations/invoices.realisation'
import { companies } from '@/core/realisations/companies.realisation'
import { orders } from '@/core/realisations/orders.realisation'

// composable
import { useMaska } from '@/common/composable/useMaska'
import useNotifications from '@/common/composable/useNotifications'
import { DataState } from '@/core/classes/DataState.class'
import useAccount from '@/common/composable/useAccount'
import { EMPTY_ADDITIONAL_SERVICES } from '@/core/constants/additional_services.constants'
import { useFormData } from '@/common/composable/useFormData'
import { AdditionalServicesI } from '@/core/types/additional_services.types'
import { AdditionalByIdT } from '@/core/types/billing.types'

const CONTACT_CENTER_MANAGER_ROLE_ID = 49
const DEVELOPER_ROLE_ID = 11
const TECH_SUPPORT_ROLE_ID = 28

export default defineComponent({
    components: {
        'the-input': TheInput,
        'the-button': TheButton,
        'the-textarea': TheTextarea,
        'the-select': TheSelect,
        checkbox: Checkbox,
        'radio-group': RadioGroup,
        loader: Loader,
        FileReader,
        UploadedFile,
    },
    props: {
        stepNumber: {
            type: Number,
            default: 0,
        },
        opened: {
            type: Boolean,
            default: false,
        },
        isUpdateCreatedCargo: {
            type: Boolean,
            default: false,
        },
        isUpdate: {
            type: Boolean,
            default: false,
        },
        isUpdateInvoice: {
            type: Boolean,
            default: false,
        },
        updatingCargoIndex: {
            type: Number,
            default: null,
        },
        updatingCargo: {
            type: Object as PropType<CompletedCargoT>,
            default: null,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        takeDate: {
            type: String,
            default: null,
        },
        periodId: {
            type: Number,
            default: null,
        },
        invoiceId: {
            type: Number,
            default: null,
        },
        companyId: {
            type: Number,
            default: null,
        },
        isNoCashInput: {
            type: Boolean,
            default: false,
        },
        senderCityId: {
            type: Number,
            default: null,
        },
        senderCityDispatcherSectorId: {
            type: Number,
            default: null
        },
        receiver: {
            type: Object as PropType<ReceiverT & { dispatcher_sector_id: number | null }>,
            default: null,
        },
        companyVerify: {
            type: Number,
            default: null,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        additionalServices: {
            type: Array as PropType<AdditionalByIdT[]>,
            default: () => [],
        },
        receiverCityType: {
            type: Number,
            default: null
        },
        isCashEnabledBySetting: {
            type: Boolean,
            default: false,
        },
        isKaspiEnabledBySetting: {
            type: Boolean,
            default: false,
        },
        isShipmentTypeExpress3HoursEnabledBySetting: {
            type: Boolean,
            default: false,
        },
        isShipmentTypeExpressDayToDayEnabledBySetting: {
            type: Boolean,
            default: false,
        }
    },
    emits: ['submit', 'cancel-update'],
    setup(props, { emit }) {
        const account = useAccount()
        const { unmaskNumbers } = useMaska()
        const notifications = useNotifications()
        const { getFormData } = useFormData()

        let additional_services: any = reactive(EMPTY_ADDITIONAL_SERVICES)

        const isAviaBlockedCargosDetailsShowed = ref(false)

        const state = reactive({
            product_name: null as string | null,
            customer_invoice_number: null as string | null,
            weight: null as number | null,
            places: null as number | null,
            depth: null as number | null,
            width: null as number | null,
            height: null as number | null,
            //
            volume: null as number | null,
            //
            annotation: null as string | null,
            is_return_documents: 0,
            is_ensure: 0,
            is_dangerous: 0,
            is_temperature_regime_active: false,
            temperature_regime_type_id: null as number | null,
            invoice_files: [] as string[],
            certificate_of_safety_files: [] as string[],
            temperature_regime_safety_files: [] as string[],
            msds_file: null as string | null,
            verify: false,
            //
            payer_company_id: null as number | null,
            cod_payment: null as string | null,
            cargo_name: null as string | null,
            declared_price: null as string | null,
            shipment_type: 1,
            payment_method: PAYMENT_METHOD_OPTIONS[0].id,
            payment_type: 1,
            cash_sum: null as string | null,
            has_battery: null as number | null,
            has_auto_parts: null as number | null,
            //
            is_admin: false,
        })

        const files_state = reactive({
            is_loading: false,
            //
            invoice_files: [] as File[],
            certificate_of_safety_files: [] as File[],
            temperature_regime_safety_files: [] as File[],
            msds_file: [] as File[]
        })

        const files_backend_state = reactive({
            invoice_files: [] as UploadedFileT[],
            certificate_of_safety_files: [] as UploadedFileT[],
            temperature_regime_safety_files: [] as UploadedFileT[],
            msds_files: [] as UploadedFileT[]
        })

        const data_state = reactive({
            payers: new DataState<PayerT>({}),
            temperature_regimes: new DataState<TemperatureRegimeT>({}),
            payment_methods: PAYMENT_METHOD_OPTIONS,
            verify: {
                type: 0 as number,
                changed_by_user: false,
                is_loading: false,
                is_failed: false,
            },
        })

        const CargoValidation = {
            product_name: { required },
            customer_invoice_number: {
                required: requiredIf(() => data_state.verify.type === 2 && state.verify),
                maxLength: maxLength(30),
            },
            declared_price: {
                required: requiredIf(() => Boolean(state.is_ensure)),
                decimal
            },
            weight: { required, decimal },
            places: { required, integer },
            depth: { required, decimal },
            width: { required, decimal },
            height: { required, decimal },
            payer_company_id: {
                required: requiredIf(
                    () =>
                        (state.payment_type === 2 && state.payment_method === 3) ||
                        state.payment_type === 3
                ),
            },
            cash_sum: { decimal },
            temperature_regime_type_id: {
                required: requiredIf(() => state.is_temperature_regime_active),
            },
            cargo_name: {
                required: requiredIf(() => Boolean(state.is_ensure) && !props.isUpdateInvoice),
                minLength: Boolean(state.is_ensure) ? minLength(3) : true,
            },
            has_battery: {
                required: requiredIf(() => (state.shipment_type === 2 && !props.isUpdateInvoice)),
            },
            has_auto_parts: {
                required: requiredIf(() => (state.shipment_type === 2 && !props.isUpdateInvoice)),
            }
        }
        const FilesValidation = {
            invoice_files: {
                required: requiredIf(
                    () => Boolean(state.is_dangerous) && !files_backend_state.invoice_files.length
                ),
            },
            certificate_of_safety_files: {
                required: requiredIf(
                    () =>
                        Boolean(state.is_dangerous) &&
                        !files_backend_state.certificate_of_safety_files.length
                ),
            },
            temperature_regime_safety_files: {
                required: requiredIf(
                    () =>
                        Boolean(state.is_temperature_regime_active) &&
                        !files_backend_state.temperature_regime_safety_files.length
                ),
            },
        }

        const state_form$ = useVuelidate(CargoValidation, state)
        const files_state_form$ = useVuelidate(FilesValidation, files_state)

        const is_loading = ref(false)

        const resetFiles = () => {
            state.invoice_files = []
            state.certificate_of_safety_files = []
            state.temperature_regime_safety_files = []
            state.msds_file = null

            files_state.invoice_files = []
            files_state.certificate_of_safety_files = []
            files_state.temperature_regime_safety_files = []
            files_state.msds_file = []
        }

        const resetForm = () => {
            additional_services.hasCar = false
            additional_services.hasSoftPackage = false
            additional_services.hasManipulator = false
            additional_services.hasCrane = false
            additional_services.hasHydraulicTrolley = false
            additional_services.hasGrid = false
            additional_services.hasLoader = false
            additional_services.hasPallet = false

            state.product_name = null
            state.customer_invoice_number = null
            state.weight = null
            state.places = null
            state.depth = null
            state.width = null
            state.height = null
            state.annotation = null
            state.is_return_documents = 0
            state.cod_payment = null
            state.cargo_name = null
            state.declared_price = null
            state.shipment_type = 1
            state.payment_type = 1
            state.payer_company_id = null
            state.cash_sum = null
            state.payment_method = PAYMENT_METHOD_OPTIONS[0].id
            state.has_auto_parts = null
            state.has_battery = null

            state.is_dangerous = 0
            state.is_temperature_regime_active = false
            state.temperature_regime_type_id = null
            resetFiles()

            state_form$.value.$reset()
            files_state_form$.value.$reset()
        }

        const uploadFiles = async () => {
            try {
                is_loading.value = true

                let invoice_files: UplodaFilesResponseT = { data: [] }
                if (files_state.invoice_files.length) {
                    invoice_files = await invoices.uploadFiles(
                        getFormData({ type: 17, files: files_state.invoice_files })
                    )
                }

                let certificate_of_safety_files: UplodaFilesResponseT = { data: [] }
                if (files_state.certificate_of_safety_files.length) {
                    certificate_of_safety_files = await invoices.uploadFiles(
                        getFormData({ type: 18, files: files_state.certificate_of_safety_files })
                    )
                }

                state.invoice_files = invoice_files.data
                state.certificate_of_safety_files = certificate_of_safety_files.data
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При загрузке файлов произошла ошбика',
                })
            } finally {
                is_loading.value = false
            }
        }

        const uploadMsdsFiles = async () => {
            try {
                is_loading.value = true

                const msds_files = await invoices.uploadFiles(getFormData({ type: 45, files: files_state.msds_file }))

                if (msds_files.data.length) {
                    state.msds_file = msds_files.data[0]
                }
            } catch(error) {
                notifications.notifyError({ error, title: 'При загрузке файлов произошла ошбика' })
            } finally {
                is_loading.value = false
            }
        }

        const uploadTemperatureFiles = async () => {
            try {
                is_loading.value = true

                let temperature_regime_safety_files: UplodaFilesResponseT = { data: [] }
                if (files_state.temperature_regime_safety_files.length) {
                    temperature_regime_safety_files = await invoices.uploadFiles(
                        getFormData({
                            type: 40,
                            files: files_state.temperature_regime_safety_files,
                        })
                    )
                }

                state.temperature_regime_safety_files = temperature_regime_safety_files.data
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При загрузке файлов произошла ошбика',
                })
            } finally {
                is_loading.value = false
            }
        }

        const emitSubmit = async () => {
            if (is_loading.value || isSelectedThirdFaceChl.value) {
                return
            }

            state_form$.value.$touch()
            files_state_form$.value.$touch()

            if (state_form$.value.$invalid || files_state_form$.value.$invalid || props.disabled) {
                return
            }

            if (!Number(state.width) || !Number(state.height) || !Number(state.depth)) {
                notifications.notify({
                    title: 'Пожалуйста, заполните ДШВ',
                    type: 'danger',
                })
                return
            }

            if (props.senderCityId === props.receiver.city_id && state.shipment_type === 2) {
                notifications.notify({
                    title: 'Город отправителя и город получателя не должны совпадать, если выбрано значение "Авиа"!',
                    type: 'danger',
                })
                return
            }

            const volume_weight =
                (Number(state.width) * Number(state.height) * Number(state.depth)) / 1000000
            state.volume = volume_weight > 0.01 ? Number(volume_weight.toFixed(2)) : 0.01

            // Если выключено и не трогали то null
            // Если включено и отказались 0
            // Если включено и приняли type
            let verify = null

            if (data_state.verify.type > 0) {
                if (state.verify) {
                    verify = data_state.verify.type
                } else {
                    verify = 0
                }
            }

            // Загрузить файлы, чтобы сохранить и получить их в формате string[]
            if (
                state.is_dangerous &&
                (files_state.invoice_files.length || files_state.certificate_of_safety_files.length)
            ) {
                await uploadFiles()
            }

            if (
                state.is_temperature_regime_active &&
                files_state.temperature_regime_safety_files.length
            ) {
                await uploadTemperatureFiles()
            }

            if (files_state.msds_file.length) {
                await uploadMsdsFiles()
            }

            if (props.isUpdateCreatedCargo && props.takeDate && props.periodId && props.invoiceId) {
                try {
                    is_loading.value = true
                    const { unmaskNumbers } = useMaska()

                    const payload: UpdateInvoicePayloadT = {
                        additional_service: additional_services as AdditionalServicesI,
                        product_name: state.product_name,
                        dop_invoice_number: state.customer_invoice_number,
                        annotation: state.annotation,
                        cod_payment: state.cod_payment
                            ? Number(unmaskNumbers(state.cod_payment))
                            : 0,
                        declared_price: state.declared_price ? Number(state.declared_price) : 0,
                        take_date: props.takeDate,
                        period_id: props.periodId,
                        places: Number(state.places),
                        weight: Number(state.weight),
                        width: Number(state.width),
                        height: Number(state.height),
                        depth: Number(state.depth),
                        volume: state.volume,
                        cargo_name: state.cargo_name,
                        should_return_document: state.is_return_documents,
                        shipment_type: state.shipment_type,
                        payment_type: state.payment_type,
                        payment_method: state.payment_method,
                        verify,
                        //
                        is_dangerous: state.is_dangerous,
                        temperature_regime_type_id: state.temperature_regime_type_id,
                        invoice_files: state.invoice_files,
                        certificate_of_safety_files: state.certificate_of_safety_files,
                        temperature_regime_safety_files: state.temperature_regime_safety_files,
                        //
                        has_battery: undefined,
                        has_auto_parts: undefined,
                        msds_file: undefined
                    }

                    if (state.payer_company_id) payload.payer_company_id = state.payer_company_id
                    if (state.cash_sum) payload.cash_sum = Number(state.cash_sum)

                    await invoices.updateInvoice(props.invoiceId, payload)

                    notifications.notify({
                        title: 'Накладная отредактирована',
                        type: 'success',
                    })

                    emit('submit', {
                        ...state,
                        temperature_regime_type_id: state.temperature_regime_type_id,
                        weight: Number(state.weight),
                        places: Number(state.places),
                        depth: Number(state.depth),
                        width: Number(state.width),
                        height: Number(state.height),
                        payer_company_id: state.payer_company_id,
                        cash_sum: Number(state.cash_sum),
                        additional_services: { ...additional_services },
                        verify,
                        invoice_files_fe: files_state.invoice_files,
                        certificate_of_safety_files_fe: files_state.certificate_of_safety_files,
                        temperature_regime_safety_files_fe:
                            files_state.temperature_regime_safety_files,
                        has_battery: undefined,
                        has_auto_parts: undefined,
                        msds_file: undefined,
                        msds_files_fe: []
                    })

                    resetForm()
                } catch (error) {
                    console.error(error)
                    notifications.notifyError({
                        error,
                        title: 'При редактировании накладной произошла ошбика',
                    })
                } finally {
                    is_loading.value = false
                }

                return
            }

            if (props.isUpdate) {
                emit('submit', {
                    ...state,
                    temperature_regime_type_id: state.temperature_regime_type_id,
                    weight: Number(state.weight),
                    places: Number(state.places),
                    depth: Number(state.depth),
                    width: Number(state.width),
                    height: Number(state.height),
                    payer_company_id: state.payer_company_id,
                    cash_sum: state.cash_sum ? unmaskNumbers(state.cash_sum) : '',
                    additional_services: { ...additional_services },
                    verify,
                    invoice_files_fe: files_state.invoice_files,
                    certificate_of_safety_files_fe: files_state.certificate_of_safety_files,
                    temperature_regime_safety_files_fe: files_state.temperature_regime_safety_files,
                    has_battery: state.has_battery === 1 ? 1 : state.has_battery === 2 ? 0 : null,
                    has_auto_parts: state.has_auto_parts === 1 ? 1 : state.has_auto_parts === 2 ? 0 : null,
                    msds_file: state.msds_file,
                    msds_files_fe: files_state.msds_file
                })

                resetForm()
                return
            }

            emit('submit', {
                ...state,
                temperature_regime_type_id: state.temperature_regime_type_id,
                weight: Number(state.weight),
                places: Number(state.places),
                depth: Number(state.depth),
                width: Number(state.width),
                height: Number(state.height),
                payer_company_id: state.payer_company_id,
                cash_sum: state.cash_sum ? unmaskNumbers(state.cash_sum) : '',
                additional_services: { ...additional_services },
                verify,
                invoice_files_fe: files_state.invoice_files,
                certificate_of_safety_files_fe: files_state.certificate_of_safety_files,
                temperature_regime_safety_files_fe: files_state.temperature_regime_safety_files,
                msds_files_fe: files_state.msds_file,
                has_battery: state.has_battery === 1 ? 1 : state.has_battery === 2 ? 0 : null,
                has_auto_parts: state.has_auto_parts === 1 ? 1 : state.has_auto_parts === 2 ? 0 : null,
            })

            resetForm()
        }

        const emitCancelUpdate = () => {
            resetForm()
            emit('cancel-update')
        }

        watch(
            () => props.updatingCargo,
            (newest) => {
                if (!newest) return
                const cargo = toRaw(newest.logistics_info)

                additional_services.hasCar = cargo.additional_services.hasCar
                additional_services.hasSoftPackage = cargo.additional_services.hasSoftPackage
                additional_services.hasManipulator = cargo.additional_services.hasManipulator
                additional_services.hasCrane = cargo.additional_services.hasCrane
                additional_services.hasHydraulicTrolley =
                    cargo.additional_services.hasHydraulicTrolley
                additional_services.hasGrid = cargo.additional_services.hasGrid
                additional_services.hasLoader = cargo.additional_services.hasLoader
                additional_services.hasPallet = cargo.additional_services.hasPallet

                state.product_name = cargo.product_name
                state.customer_invoice_number = cargo.customer_invoice_number
                state.weight = cargo.weight
                state.places = cargo.places
                state.depth = cargo.depth
                state.width = cargo.width
                state.height = cargo.height
                state.annotation = cargo.annotation
                state.is_return_documents = cargo.is_return_documents
                state.cod_payment = cargo.cod_payment
                state.cargo_name = cargo.cargo_name
                state.declared_price = cargo.declared_price

                if (Number(state.declared_price)) {
                    state.is_ensure = 1
                }

                state.shipment_type = cargo.shipment_type
                state.payer_company_id = cargo.payer_company_id
                state.cash_sum = cargo.cash_sum?.toString() || null
                state.payment_method = cargo.payment_method

                state.is_dangerous = cargo.is_dangerous
                state.is_temperature_regime_active = Boolean(cargo.temperature_regime_type_id)
                state.temperature_regime_type_id = cargo.temperature_regime_type_id

                state.invoice_files = cargo.invoice_files as string[]
                state.certificate_of_safety_files = cargo.certificate_of_safety_files as string[]
                state.temperature_regime_safety_files =
                    cargo.temperature_regime_safety_files as string[]

                files_state.invoice_files = cargo.invoice_files_fe as File[]
                files_state.certificate_of_safety_files = cargo.certificate_of_safety_files_fe as File[]
                files_state.temperature_regime_safety_files = cargo.temperature_regime_safety_files_fe as File[]
                files_state.msds_file = cargo.msds_files_fe as File[]

                state.has_auto_parts = cargo.has_auto_parts === 1 ? 1 : cargo.has_auto_parts === 0 ? 2 : null
                state.has_battery = cargo.has_battery === 1 ? 1 : cargo.has_battery === 0 ? 2 : null
                state.msds_file = cargo.msds_file ? cargo.msds_file as string : null

                state.payment_type = cargo.payment_type
                onPaymentTypeChange()
            }
        )

        const loadPayers = async () => {
            try {
                data_state.payers.is_loading = true
                const response = await companies.getPayers(1, props.companyId)
                data_state.payers.list = response.data

                data_state.payers.is_failed = false
                data_state.payers.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.payers.failure.catchError(error)

                if (!data_state.payers.failure.error_state.is_cancel) {
                    data_state.payers.is_failed = true
                }
            } finally {
                if (!data_state.payers.failure.error_state.is_cancel) {
                    data_state.payers.is_loading = false
                }
            }
        }

        const loadTemperatureRegimes = async () => {
            try {
                data_state.temperature_regimes.is_loading = true
                const response = await orders.getTemperatureRegimes()
                data_state.temperature_regimes.list = response.data

                data_state.temperature_regimes.is_failed = false
                data_state.temperature_regimes.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.temperature_regimes.failure.catchError(error)

                if (!data_state.temperature_regimes.failure.error_state.is_cancel) {
                    data_state.temperature_regimes.is_failed = true
                }
            } finally {
                if (!data_state.temperature_regimes.failure.error_state.is_cancel) {
                    data_state.temperature_regimes.is_loading = false
                }
            }
        }

        //

        const setAdditionalServices = () => {
            const additional_services_values: any =
                props.updatingCargo.logistics_info.additional_services

            for (const service_name in additional_services_values) {
                additional_services[service_name] = additional_services_values[service_name]
            }
        }

        const setCargo = () => {
            if (props.isUpdate && props.updatingCargo) {
                const cargo = props.updatingCargo.logistics_info

                setAdditionalServices()

                state.product_name = cargo.product_name
                state.customer_invoice_number = cargo.customer_invoice_number
                state.weight = cargo.weight
                state.places = cargo.places
                state.depth = cargo.depth
                state.width = cargo.width
                state.height = cargo.height
                state.annotation = cargo.annotation
                state.is_return_documents = cargo.is_return_documents
                state.cod_payment = cargo.cod_payment
                state.cargo_name = cargo.cargo_name
                state.declared_price = cargo.declared_price

                if (Number(state.declared_price)) {
                    state.is_ensure = 1
                }

                state.shipment_type = cargo.shipment_type
                state.payer_company_id = cargo.payer_company_id
                state.cash_sum = cargo.cash_sum?.toString() || null
                state.payment_method = cargo.payment_method

                state.is_dangerous = cargo.is_dangerous
                state.is_temperature_regime_active = Boolean(cargo.temperature_regime_type_id)
                state.temperature_regime_type_id = cargo.temperature_regime_type_id

                files_backend_state.invoice_files = cargo.invoice_files as UploadedFileT[]
                files_backend_state.certificate_of_safety_files = cargo.certificate_of_safety_files as UploadedFileT[]
                files_backend_state.temperature_regime_safety_files = cargo.temperature_regime_safety_files as UploadedFileT[]

                // if (cargo.msds_file) {
                //     files_backend_state.msds_files = [cargo.msds_file as UploadedFileT]
                // }

                state.has_auto_parts = cargo.has_auto_parts === 1 ? 1 : cargo.has_auto_parts === 0 ? 2 : null
                state.has_battery = cargo.has_battery === 1 ? 1 : cargo.has_battery === 0 ? 2 : null

                state.payment_type = cargo.payment_type
                onPaymentTypeChange()
            }
        }

        const deleteFile = async ({ id, index }: { id: number; index: number }, type: string) => {
            try {
                files_state.is_loading = true
                await invoices.deleteFile(id)

                if (type === 'invoice') {
                    files_backend_state.invoice_files.splice(index, 1)
                } else if (type === 'certificate') {
                    files_backend_state.certificate_of_safety_files.splice(index, 1)
                } else if (type === 'temperature') {
                    files_backend_state.temperature_regime_safety_files.splice(index, 1)
                } else if (type === 'msds') {
                    files_backend_state.msds_files.splice(index, 1)
                }
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При удалении файла произошла ошибка',
                })
            } finally {
                files_state.is_loading = false
            }
        }

        const onPaymentTypeChange = async () => {
            if (state.payment_method && !getPaymentMethods.value.some((pm) => pm.id === state.payment_method)) {
                state.payment_method = getPaymentMethods.value[0].id
            }

            // SPL-10149
            if (account.haveRole(CONTACT_CENTER_MANAGER_ROLE_ID) || account.haveRole(DEVELOPER_ROLE_ID) || account.haveRole(TECH_SUPPORT_ROLE_ID)) {
                if (state.payment_type === 1) {
                    await loadPayers()

                    if (state.payment_method === 2 || state.payment_method === 4) {
                        const chlPayers = data_state.payers.list.filter((p) => p.isChl)

                        if (chlPayers.length > 0) {
                            state.payer_company_id = chlPayers[0].id
                        }
                    } else {
                        state.payer_company_id = null
                    }

                    return
                }
            }

            if (state.payment_type === 3) {
                loadPayers()
                return
            }

            if (state.payment_type === 2) {
                await loadPayers()

                if (state.payment_method === 2 || state.payment_method === 4) {
                    const chlPayers = data_state.payers.list.filter((p) => p.isChl)

                    if (chlPayers.length > 0) {
                        state.payer_company_id = chlPayers[0].id
                    }
                } else {
                    state.payer_company_id = null
                }

                return
            }

            state.payer_company_id = null
        }

        const onPaymentMethodChange = () => {
            if (state.payment_method === 3) state.cash_sum = null

            if (state.payment_method === 2 || state.payment_method === 4) {
                const chlPayers = data_state.payers.list.filter((p) => p.isChl)

                if (chlPayers.length > 0) {
                    state.payer_company_id = chlPayers[0].id
                }
            } else {
                state.payer_company_id = null
            }
        }

        const onIsDangerousChange = (is_dangerous: number) => {
            if (is_dangerous === 0) resetFiles()
        }

        const isOnPayerSelect = computed(() => {
            if (account.haveRole(CONTACT_CENTER_MANAGER_ROLE_ID) || account.haveRole(DEVELOPER_ROLE_ID) || account.haveRole(TECH_SUPPORT_ROLE_ID)) {
                if (state.payment_type === 1) {
                    if (state.payment_method === 2 || state.payment_method === 4) {
                        return data_state.payers.list.filter((p) => p.isChl).length > 0
                    }

                    if (state.payment_method === 3) {
                        return true
                    }
                }
            }

            if (state.payment_type === 2) {
                if (state.payment_method === 2 || state.payment_method === 4) {
                    return data_state.payers.list.filter((p) => p.isChl).length > 0
                }

                if (state.payment_method === 3) {
                    return true
                }

                return false
            }

            return state.payment_type === 3
        })

        const isOnCashInput = computed(
            () => state.payment_method === 2 || state.payment_method === 4
        )

        const onPayerSelect = (payer: number | null) => {
            if (!payer) return
            // update payment methods
        }

        const onTemperatureRegimeChanged = (is_checked: boolean) => {
            if (!is_checked) state.temperature_regime_type_id = null
        }

        const onChangeShipmentType = (shipment_type: number) => {
            if (shipment_type === 1) {
                state.has_battery = null
                state.has_auto_parts = null
                state.msds_file = null
                files_state.msds_file = []
                files_backend_state.msds_files =  []
            }
        }

        // const loadVerifyType = async () => {
        //     try {
        //         data_state.verify.is_loading = true

        //         const response = await users.getProfile()

        //         data_state.verify.type = response.data.company.verify

        //         // if (data_state.verify.type) {
        //         //     state.verify = true
        //         // }

        //         data_state.verify.is_failed = false
        //     } catch (error) {
        //         notifications.notifyError({
        //             error,
        //             title: 'При получении типа верификации произошла ошибка',
        //         })
        //         data_state.verify.is_failed = true
        //     } finally {
        //         data_state.verify.is_loading = false
        //     }
        // }

        const customInvoiceNumberPlaceholder = computed(() => {
            if (data_state.verify.type === 2 && state.verify) return 'Номер накладной Kaspi'
            return 'Номер накладной заказчика'
        })

        const ltlShipmentTypes = computed(() => {
            if (props.receiver && props.receiver.city_id === props.senderCityId) {
                const types = LTL_SHIPMENT_TYPES.filter((type) => type.id !== 2)

                if (props.isShipmentTypeExpress3HoursEnabledBySetting) {
                    types.push({ id: 5, name: 'Экспресс за 3 часа' })
                }

                if (props.isShipmentTypeExpressDayToDayEnabledBySetting) {
                    types.push({ id: 6, name: 'Экспресс день в день' })
                }

                return types
            }

            if (props.senderCityDispatcherSectorId && props.receiver && props.senderCityDispatcherSectorId === props.receiver.dispatcher_sector_id) {
                return LTL_SHIPMENT_TYPES.filter((type) => type.id !== 2)
            }

            return LTL_SHIPMENT_TYPES
        })

        const isCanInputCash = computed(() => {
            return (
                isOnCashInput.value &&
                !props.isNoCashInput &&
                (state.is_admin || account.havePermission('all-root-permission'))
            )
        })

        const isInvoiceFiles = computed(() => {
            return props.isUpdateInvoice && files_backend_state.invoice_files.length
        })

        const isCertificate = computed(() => {
            return props.isUpdateInvoice && files_backend_state.certificate_of_safety_files.length
        })

        const isTemperature = computed(() => {
            return (
                props.isUpdateInvoice && files_backend_state.temperature_regime_safety_files.length
            )
        })

        const isMsds = computed(() => {
            return props.isUpdateInvoice && files_backend_state.msds_files.length
        })

        const getPaymentMethods = computed(() => {
            // SPL-10149
            if (account.haveRole(CONTACT_CENTER_MANAGER_ROLE_ID) || account.haveRole(DEVELOPER_ROLE_ID) || account.haveRole(TECH_SUPPORT_ROLE_ID)) {
                return data_state.payment_methods
            }

            if (state.payment_type === 1) {
                return data_state.payment_methods.filter((p) => p.id === 3)
            }

            if (state.payment_type === 3) {
                return data_state.payment_methods.filter((p) => p.id === 3)
            }

            if (props.receiverCityType && props.receiverCityType === 3) {
                if (state.payment_type === 2) {
                    if (props.isCashEnabledBySetting && props.isKaspiEnabledBySetting) {
                        return data_state.payment_methods
                    }

                    if (!props.isCashEnabledBySetting && props.isKaspiEnabledBySetting) {
                        return data_state.payment_methods.filter((p) => p.id === 3 || p.id === 4)
                    }

                    if (props.isCashEnabledBySetting && !props.isKaspiEnabledBySetting) {
                        return data_state.payment_methods.filter((p) => p.id === 3 || p.id === 2)
                    }

                    return data_state.payment_methods.filter((p) => p.id === 3)
                }

                return data_state.payment_methods.filter((p) => p.id === 3 || p.id === 4)
            }

            return data_state.payment_methods
        })

        const getValue = (service_name: string, key: keyof AdditionalByIdT) => {
            const service = props.additionalServices.find((s) => s.name === service_name)
            if (!service) return '-'
            return service[key]
        }

        const isSelectedThirdFaceChl = computed(() => {
            if ((state.payment_type === 3 || state.payment_type === 2) && state.payment_method === 3) {
                if (state.payer_company_id) {
                    const payer = data_state.payers.list.find((p) => p.id === state.payer_company_id)

                    if (payer) {
                        return payer.isChl
                    }
                }
            }

            return false
        })

        const isPayerSelectDisabled = computed(() => {
            if (state.payment_type === 2) {
                if (state.payment_method === 2 || state.payment_method === 4) {
                    return data_state.payers.list.length > 0
                }
            }

            return false
        })

        //

        onMounted(async () => {
            state.is_admin = account.haveRole(11)
            loadTemperatureRegimes()
            setCargo()
        })

        //

        watch(
            () => props.companyVerify,
            (newest) => {
                if (newest === null) {
                    data_state.verify.type = 0
                } else {
                    data_state.verify.type = newest

                    if (data_state.verify.type) {
                        state.verify = true
                    }
                }
            }
        )

        //

        return {
            LTL_SHIPMENT_TYPES,
            PAYMENT_TYPE_OPTIONS,
            isAviaBlockedCargosDetailsShowed,
            additional_services,
            state,
            files_state,
            files_backend_state,
            data_state,
            //
            state_form$,
            files_state_form$,
            //
            emitSubmit,
            emitCancelUpdate,
            PRICE_MASK,
            is_loading,
            loadPayers,
            loadTemperatureRegimes,
            isOnPayerSelect,
            isOnCashInput,
            onPaymentTypeChange,
            onPaymentMethodChange,
            onPayerSelect,
            onTemperatureRegimeChanged,
            onChangeShipmentType,
            deleteFile,
            onIsDangerousChange,
            getValue,
            //
            customInvoiceNumberPlaceholder,
            ltlShipmentTypes,
            isCanInputCash,
            isInvoiceFiles,
            isCertificate,
            isTemperature,
            isMsds,
            getPaymentMethods,
            isSelectedThirdFaceChl,
            isPayerSelectDisabled
        }
    },
})
